import i18n from './index'

/**
 * 翻译文本的工具函数
 * @param key - 翻译的 key
 * @param params - 替换参数
 */
export function t(key: string, params?: Record<string, any>) {
  return i18n.t(key, params) as string
}

/**
 * 创建 commonTexts 对象的工厂函数
 * 用于将多个 key 映射为一个对象
 *
 * @param keyMap - key 映射对象,value 可以是:
 *   - string: i18n key,会自动翻译
 *   - null/undefined/空字符串: 保持为固定值,不翻译
 *
 * @example
 * ```ts
 * createCommonTexts({
 *   loading: 'Loading',        // 会翻译
 *   fontSizeDecrease: 'A-',    // 固定值,不翻译
 * })
 * ```
 */
export function createCommonTexts<T extends Record<string, string | null | undefined>>(
  keyMap: T,
): Record<keyof T, string> {
  const result = {} as Record<keyof T, string>

  for (const [localKey, value] of Object.entries(keyMap)) {
    // 如果 value 为空字符串、null 或 undefined,则作为固定值处理
    if (!value || value === '') {
      Object.defineProperty(result, localKey, {
        value: value || '',
        writable: false,
        enumerable: true,
        configurable: true,
      })
    }
    else {
      // 否则作为 i18n key 处理
      Object.defineProperty(result, localKey, {
        get() {
          return t(value)
        },
        enumerable: true,
        configurable: true,
      })
    }
  }

  return result
}

/**
 * 创建带固定值的 commonTexts 对象
 * 支持混合使用 i18n key 和固定值
 *
 * @param keyMap - key 映射对象
 * @param fixedValues - 固定值对象,这些值不会被翻译
 *
 * @example
 * ```ts
 * createCommonTextsWithFixed(
 *   { loading: 'Loading', title: 'Title' },
 *   { fontSizeDecrease: 'A-', fontSizeIncrease: 'A+' }
 * )
 * ```
 */
export function createCommonTextsWithFixed<
  T extends Record<string, string>, F extends Record<string, string>,
>(
  keyMap: T,
  fixedValues: F,
): Record<keyof T | keyof F, string> {
  const result = {} as Record<keyof T | keyof F, string>

  // 处理需要翻译的 key
  for (const [localKey, i18nKey] of Object.entries(keyMap)) {
    Object.defineProperty(result, localKey, {
      get() {
        return t(i18nKey)
      },
      enumerable: true,
      configurable: true,
    })
  }

  // 处理固定值
  for (const [localKey, fixedValue] of Object.entries(fixedValues)) {
    Object.defineProperty(result, localKey, {
      value: fixedValue,
      writable: false,
      enumerable: true,
      configurable: true,
    })
  }

  return result
}
